import React, { useEffect, useState } from 'react';
import { Box, Typography, Skeleton, Button } from '@mui/material';
import { getSteamGameDetails, getSteamReviews } from '../../services/SteamApi';
import type { SteamGameDetails, SteamReviews } from '../../services/SteamApi';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ComputerIcon from '@mui/icons-material/Computer';
import LaunchIcon from '@mui/icons-material/Launch';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';

export interface NintendoStore {
  nsuid: string;
  title: string;
  image: string | null;
  icon: string | null;
  short: string | null;
  platform: string | null;  // HAC=Switch BEE=Switch2 CTR/KTR=3DS WIIU=WiiU
  store_url: string | null;  // 現行ストア(Switch/Switch2)以外は null
  price: {
    list: number | null;
    current: number | null;
    sale: boolean;  // セール期間(sale_end)で検証済み。期間外なら false で定価が入る
    sale_end: string | null;  // セール終了日時(JST)。セール中でなければ null
  } | null;  // 3DS/WiiUなど閉鎖ストアは null
  msrp: number | null;  // レガシー機の税込希望小売価格（参考表示用）
  msrp_package: boolean;  // msrpがパッケージ版定価(pprice)由来か
}

interface StoreSectionProps {
  steamAppId?: string;
  xboxId?: string;
  psId?: string;
  nintendo?: NintendoStore | null;
  currentPlayers?: number;
  gameName?: string;
}

// Steamの価格はセント単位(/100)で渡ってくる
const formatSteamPrice = (price: number): string =>
  new Intl.NumberFormat('ja-JP', { style: 'currency', currency: 'JPY', minimumFractionDigits: 0 }).format(price / 100);

// Nintendoの価格は円そのもの
const formatYen = (price: number): string =>
  new Intl.NumberFormat('ja-JP', { style: 'currency', currency: 'JPY', minimumFractionDigits: 0 }).format(price);

// Nintendoの機種コード → 表示名（他ストア行と揃えて英語短縮表記）
const nintendoPlatformLabel = (platform: string | null): string => {
  switch (platform) {
    case 'HAC': return 'Switch';
    case 'BEE': return 'Switch 2';
    case 'CTR':
    case 'KTR': return '3DS';
    case 'WIIU': return 'Wii U';
    default: return 'Nintendo';
  }
};

interface StoreRowProps {
  icon?: React.ReactNode;
  label: string;
  url?: string | null;
  priceNode: React.ReactNode;
  extra?: React.ReactNode;  // ストア固有の補足（Steamの評価/プレイ人数など）を下段にぶら下げる
}

// アイコン(18) + gap(6) 分だけ字下げして補足行をラベルに揃える
const ROW_INDENT = '24px';

// ストア1行（アイコン＋名称＋価格。行全体がストアへのリンク。extra があれば下段に補足）
const StoreRow: React.FC<StoreRowProps> = ({ icon, label, url, priceNode, extra }) => (
  <Box
    component={url ? 'a' : 'div'}
    {...(url ? { href: url, target: '_blank', rel: 'noopener noreferrer' } : {})}
    sx={{
      display: 'block',
      py: 0.25,
      px: 1.25,
      borderRadius: '8px',
      backgroundColor: 'rgba(255, 255, 255, 0.045)',
      border: '0px solid rgba(255, 255, 255, 0.07)',
      textDecoration: 'none',
      color: 'inherit',
      transition: 'background-color 0.2s ease, border-color 0.2s ease',
      '&:hover': url ? { backgroundColor: 'rgba(255, 255, 255, 0.08)', borderColor: 'rgba(255, 255, 255, 0.14)' } : {},
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
      {icon && <Box sx={{ display: 'flex', alignItems: 'center', width: 18, height: 18, flexShrink: 0 }}>{icon}</Box>}
      <Typography variant="body2" sx={{ fontSize: '0.7rem', color: '#ffffffe8', fontWeight: 500, whiteSpace: 'nowrap', flexShrink: 0 }}>
        {label}
      </Typography>
      <Box sx={{ flexGrow: 1, minWidth: 8 }} />
      <Box sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}>{priceNode}</Box>
      {url && <LaunchIcon sx={{ fontSize: 13, color: 'text.secondary', ml: 0.25, flexShrink: 0 }} />}
    </Box>
    {extra && <Box sx={{ pl: ROW_INDENT, mt: 0.5 }}>{extra}</Box>}
  </Box>
);

const SaleBadge: React.FC<{ percent: number }> = ({ percent }) => (
  <Typography
    component="span"
    sx={{
      bgcolor: 'rgba(190, 238, 17, 0.2)',
      color: '#BEEE11',
      px: 0.75,
      py: 0.25,
      borderRadius: 1,
      fontSize: '0.65rem',
      fontWeight: 600,
      lineHeight: 1,
    }}
  >
    {`-${percent}%`}
  </Typography>
);

const StoreSection: React.FC<StoreSectionProps> = ({ steamAppId, xboxId, psId, nintendo, currentPlayers, gameName }) => {
  const [steamDetails, setSteamDetails] = useState<SteamGameDetails | null>(null);
  const [reviewDetails, setReviewDetails] = useState<SteamReviews | null>(null);
  const [loading, setLoading] = useState(!!steamAppId);

  useEffect(() => {
    if (!steamAppId) {
      setLoading(false);
      return;
    }
    let active = true;
    const fetchData = async () => {
      setLoading(true);
      try {
        const [details, reviews] = await Promise.all([
          getSteamGameDetails(steamAppId),
          getSteamReviews(steamAppId),
        ]);
        if (!active) return;
        setSteamDetails(details);
        setReviewDetails(reviews.global);
      } catch (error) {
        console.error('Steamの情報取得に失敗しました:', error);
      } finally {
        if (active) setLoading(false);
      }
    };
    fetchData();
    return () => {
      active = false;
    };
  }, [steamAppId]);

  if (loading) {
    return (
      <Box sx={{ p: 2, backgroundColor: 'rgba(255, 255, 255, 0.03)', borderRadius: 1, border: '1px solid rgba(255, 255, 255, 0.05)' }}>
        <Skeleton variant="rectangular" width="100%" height={150} />
        <Skeleton variant="text" sx={{ mt: 1 }} />
        <Skeleton variant="text" />
        <Skeleton variant="text" width="60%" />
      </Box>
    );
  }

  // 表示できるストアが一つも無ければ何も出さない
  const hasSteam = !!steamDetails;
  const hasNintendo = !!nintendo;
  if (!hasSteam && !hasNintendo) {
    return null;
  }

  // 上部の情報ブロックは代表ストア（Steamがあれば優先、無ければNintendo）から
  const headerImage = steamDetails?.header_image || nintendo?.image || undefined;
  const shortDescription = steamDetails?.short_description || nintendo?.short || '';
  const comingSoon = steamDetails?.release_date?.coming_soon ?? false;
  const marketplaceName = steamDetails?.name || nintendo?.title || gameName || '';

  // 価格行として出せるもの（Steam / 現行ストアのNintendo / レガシーの希望小売価格）があるか
  const hasNintendoStore = !!(nintendo && nintendo.store_url && nintendo.price);
  const hasNintendoMsrp = !!(nintendo && !nintendo.store_url && nintendo.msrp);
  const hasPriceRows = hasSteam || hasNintendoStore || hasNintendoMsrp;

  // Nintendo側のセールフラグはクロール時点のスナップショットで、終了後も次の取得まで
  // 立ったまま残る。終了日時を過ぎていたらセール扱いしない（定価表示に戻す）
  const nintendoSaleEnd = nintendo?.price?.sale_end ? new Date(nintendo.price.sale_end) : null;
  const nintendoSaleLive = !!nintendo?.price?.sale && (!nintendoSaleEnd || nintendoSaleEnd.getTime() > Date.now());

  // セール中はバッジ＋二重価格で右側が詰まるのでラベルを短縮する
  const nintendoOnSale = !!(
    hasNintendoStore && nintendoSaleLive &&
    nintendo?.price?.current != null && nintendo.price.list != null &&
    nintendo.price.current < nintendo.price.list
  );

  // Steam固有の指標（評価・プレイ人数）はSteam行の下段にぶら下げる
  const hasReview = !!reviewDetails && !comingSoon && reviewDetails.total_reviews > 0;
  const reviewLabelMap: Record<string, string> = {
    'Overwhelmingly Positive': '圧倒的に好評',
    'Very Positive': '非常に好評',
    'Positive': '好評',
    'Mostly Positive': 'やや好評',
    'Mixed': '賛否両論',
    'Mostly Negative': 'やや不評',
    'Negative': '不評',
    'Very Negative': '非常に不評',
    'Overwhelmingly Negative': '圧倒的に不評',
  };
  const reviewColor = reviewDetails
    ? reviewDetails.review_score >= 6 ? '#66BB6A' : reviewDetails.review_score >= 4 ? '#FFA726' : '#EF5350'
    : 'inherit';
  const positivePct = hasReview ? Math.round((reviewDetails!.total_positive / reviewDetails!.total_reviews) * 100) : 0;

  const steamExtra = (hasReview || currentPlayers !== undefined) ? (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 1, rowGap: 0.5, fontSize: '0.62rem', color: 'text.secondary', lineHeight: 1 }}>
      {hasReview && reviewDetails && (
        <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
          <Box component="span" sx={{ color: reviewColor, fontWeight: 500 }}>
            {reviewLabelMap[reviewDetails.review_score_desc] || reviewDetails.review_score_desc}
          </Box>
          <ThumbUpIcon sx={{ fontSize: '0.62rem' }} />
          <Box component="span" sx={{ color: '#ffffffe8' }}>{positivePct}%</Box>
          <Box component="span">({new Intl.NumberFormat('ja-JP').format(reviewDetails.total_reviews)}件)</Box>
        </Box>
      )}
      {currentPlayers !== undefined && (
        <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
          <ComputerIcon sx={{ fontSize: '0.72rem', opacity: 0.85 }} />
          <Box component="span" sx={{ color: 'primary.main' }}>{new Intl.NumberFormat('ja-JP').format(currentPlayers)}人</Box>
          プレイ中
        </Box>
      )}
    </Box>
  ) : null;

  return (
    <Box sx={{ backgroundColor: 'rgba(255, 255, 255, 0.03)', borderRadius: 1, border: '1px solid rgba(255, 255, 255, 0.05)', overflow: 'hidden' }}>
      {headerImage && (
        <Box component="img" src={headerImage} alt={marketplaceName} sx={{ width: '100%', height: 'auto', display: 'block' }} />
      )}

      <Box sx={{ p: 1.5 }}>
        {shortDescription && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              fontSize: '0.65rem',
              mb: 1.5,
              lineHeight: '1.25',
              display: '-webkit-box',
              WebkitLineClamp: 5,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {shortDescription}
          </Typography>
        )}

        {/* ストア別 価格行リスト（ストア固有の指標は各行の下段に付く） */}
        {hasPriceRows && (
        <Box sx={{ my: 1, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          {hasSteam && (
            <StoreRow
              icon={<img src="/icons/service/steam.svg" alt="Steam" style={{ width: 16, height: 16, opacity: 0.9 }} />}
              label="Steam"
              url={`https://store.steampowered.com/app/${steamAppId}/`}
              extra={steamExtra}
              priceNode={
                steamDetails?.is_free ? (
                  <Typography sx={{ fontSize: '0.75rem', color: '#ffffffe8', fontWeight: 500 }}>無料</Typography>
                ) : steamDetails?.price_overview ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    {steamDetails.price_overview.discount_percent > 0 && (
                      <SaleBadge percent={steamDetails.price_overview.discount_percent} />
                    )}
                    <Typography
                      component="span"
                      sx={{
                        color: steamDetails.price_overview.discount_percent > 0 ? '#BEEE11' : '#ffffffe8',
                        fontSize: '0.85rem',
                        fontWeight: 600,
                      }}
                    >
                      {formatSteamPrice(steamDetails.price_overview.final)}
                    </Typography>
                    {steamDetails.price_overview.discount_percent > 0 && (
                      <Typography component="span" sx={{ textDecoration: 'line-through', color: 'text.secondary', fontSize: '0.65rem' }}>
                        {formatSteamPrice(steamDetails.price_overview.initial)}
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>価格情報なし</Typography>
                )
              }
            />
          )}

          {hasNintendoStore && nintendo && nintendo.price && (
            <StoreRow
              icon={<img src="/icons/service/nintendo_store_desaturate.png" alt="Nintendo Store" style={{ width: 16, height: 16, borderRadius: 3, objectFit: 'cover' }} />}
              label={nintendoOnSale ? 'Nintendo' : 'Nintendo Store'}
              url={nintendo.store_url}
              priceNode={(() => {
                const { list, current } = nintendo.price;
                const onSale = nintendoOnSale;
                const discountPercent = onSale ? Math.round((1 - (current as number) / (list as number)) * 100) : 0;
                if (list == null && current == null) {
                  return <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>価格情報なし</Typography>;
                }
                return (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    {onSale && discountPercent > 0 && <SaleBadge percent={discountPercent} />}
                    <Typography component="span" sx={{ color: onSale ? '#BEEE11' : '#ffffffe8', fontSize: '0.85rem', fontWeight: 600 }}>
                      {formatYen((onSale ? current : (list ?? current)) as number)}
                    </Typography>
                    {onSale && list != null && (
                      <Typography component="span" sx={{ textDecoration: 'line-through', color: 'text.secondary', fontSize: '0.65rem' }}>
                        {formatYen(list)}
                      </Typography>
                    )}
                  </Box>
                );
              })()}
            />
          )}

          {hasNintendoMsrp && nintendo && nintendo.msrp && (
            <StoreRow
              icon={nintendo.msrp_package
                ? <Inventory2OutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                : <FileDownloadOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />}
              label={nintendoPlatformLabel(nintendo.platform)}
              priceNode={
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                  <Typography variant="body2" component="span" sx={{ fontSize: '0.58rem', color: 'text.secondary' }}>希望小売価格</Typography>
                  <Typography component="span" sx={{ fontSize: '0.85rem', color: '#ffffffe8', fontWeight: 600 }}>
                    {formatYen(nintendo.msrp)}
                  </Typography>
                </Box>
              }
            />
          )}
        </Box>
        )}

        {/* 二次流通・検索リンク */}
        <Box
          sx={{
            mt: 1,
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: 0.75,
            '& .MuiButton-root': {
              minWidth: 'auto',
              borderRadius: '6px',
              transition: 'all 0.2s ease',
              backgroundColor: 'rgba(255, 255, 255, 0.06)',
              backdropFilter: 'blur(8px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              textTransform: 'none',
              padding: '2px 12px',
              color: 'text.secondary',
              lineHeight: '1',
              fontSize: '0.65rem',
              fontWeight: 500,
              letterSpacing: '0.02em',
              '& .MuiButton-startIcon': {
                margin: 0,
                paddingRight: '8px',
                '& > *:nth-of-type(1)': { fontSize: 20 },
              },
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.12)',
                transform: 'translateY(-1px)',
                '& img': { opacity: 1 },
              },
            },
          }}
        >
          {psId && (
            <Button
              variant="contained"
              size="small"
              startIcon={<img src="/icons/service/playstation_color.svg" alt="PlayStation" style={{ width: 20, height: 20, opacity: 1 }} />}
              component="a"
              href={`https://store.playstation.com/ja-jp/concept/${psId}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              PlayStation
            </Button>
          )}
          {xboxId && (
            <Button
              variant="contained"
              size="small"
              startIcon={<img src="/icons/service/xbox_color.svg" alt="Xbox" style={{ width: 18, height: 18, opacity: 1 }} />}
              component="a"
              href={`https://www.microsoft.com/ja-jp/p/product/${xboxId}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Xbox
            </Button>
          )}
          <Button
            variant="contained"
            size="small"
            startIcon={<img src="/icons/service/amazon.svg" alt="Amazon" style={{ width: 20, height: 20, filter: 'brightness(0) saturate(100%) invert(71%) sepia(86%) saturate(1857%) hue-rotate(360deg) brightness(103%) contrast(106%)', opacity: 0.9 }} />}
            component="a"
            href={`https://www.amazon.co.jp/s?k=${encodeURIComponent(marketplaceName)}&tag=pulse-db-22`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Amazon
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<img src="/icons/service/rakuten.svg" alt="Rakuten" style={{ width: 20, height: 20, opacity: 1 }} />}
            component="a"
            href={`https://hb.afl.rakuten.co.jp/hgc/105f4abe.0bb38550.105f4abf.cdf46e34/?pc=https%3A%2F%2Fsearch.rakuten.co.jp%2Fsearch%2Fmall%2F${encodeURIComponent(marketplaceName)}%2F101205%2F&link_type=hybrid_url&ut=eyJwYWdlIjoidXJsIiwidHlwZSI6Imh5YnJpZF91cmwiLCJjb2wiOjF9`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Rakuten
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<img src="/icons/service/mercari.svg" alt="Mercari" style={{ width: 20, height: 20, padding: 1, opacity: 0.8 }} />}
            component="a"
            href={`https://jp.mercari.com/search?afid=7418318322&category_id=76&keyword=${encodeURIComponent(marketplaceName)}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Mercari
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default StoreSection;
